import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import T from "./EditableText";

const navItems = [
    { to: "/", labelKey: "nav.start", defaultLabel: "Start" },
    { to: "/unternehmen", labelKey: "nav.unternehmen", defaultLabel: "Für Unternehmen" },
    { to: "/kandidaten", labelKey: "nav.kandidaten", defaultLabel: "Für Kandidaten" },
    { to: "/ueber-uns", labelKey: "nav.ueber", defaultLabel: "Über uns" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        window.addEventListener("scroll", onScroll);
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="main-navbar"
            className={`sticky top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "backdrop-blur-xl bg-white/80 border-b border-black/5"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                <div className="flex items-center justify-between h-20">
                    <Link to="/" data-testid="navbar-logo" className="group flex items-center gap-2">
                        <div className="relative h-9 w-9 rounded-lg bg-aimploy-navy flex items-center justify-center overflow-hidden">
                            <span className="absolute inset-0 bg-gradient-to-br from-aimploy-violet/0 via-aimploy-violet/40 to-aimploy-violet/0 transition-opacity opacity-0 group-hover:opacity-100" />
                            <span className="font-display font-bold text-white text-lg relative">a.</span>
                        </div>
                        <span className="font-display font-bold text-xl text-aimploy-navy tracking-tight">
                            aimploy
                        </span>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-1">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={`nav-link-${item.labelKey}`}
                                className={({ isActive }) =>
                                    `px-4 py-2 text-sm font-medium rounded-full transition-all ${
                                        isActive
                                            ? "text-aimploy-navy bg-black/5"
                                            : "text-aimploy-muted hover:text-aimploy-navy hover:bg-black/5"
                                    }`
                                }
                            >
                                <T k={item.labelKey} as="span">{item.defaultLabel}</T>
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <Link
                            to="/kontakt"
                            data-testid="navbar-cta-kontakt"
                            className="hidden sm:inline-flex items-center gap-2 bg-aimploy-navy text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-aimploy-navy-hover transition-all hover:scale-[1.02] active:scale-[0.98]"
                        >
                            <T k="nav.cta">Erstgespräch</T>
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        <button
                            data-testid="navbar-mobile-toggle"
                            className="lg:hidden p-2 rounded-lg hover:bg-black/5"
                            onClick={() => setOpen(!open)}
                            aria-label="Menü öffnen"
                        >
                            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>

                {open && (
                    <div className="lg:hidden pb-6 border-t border-black/5 pt-4 flex flex-col gap-1" data-testid="mobile-menu">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                className={({ isActive }) =>
                                    `px-4 py-3 rounded-lg font-medium ${
                                        isActive ? "bg-aimploy-navy text-white" : "text-aimploy-navy hover:bg-black/5"
                                    }`
                                }
                            >
                                <T k={item.labelKey} as="span">{item.defaultLabel}</T>
                            </NavLink>
                        ))}
                        <Link
                            to="/kontakt"
                            className="mt-2 px-4 py-3 rounded-lg bg-aimploy-violet text-white font-semibold text-center"
                        >
                            <T k="nav.cta_mobile">Erstgespräch buchen</T>
                        </Link>
                    </div>
                )}
            </div>
        </header>
    );
}
